<template>
    <div>
        <b-modal
            ref="md-find-work-item"
            title="Pick Work Item"
            hide-footer
            @shown="onModelShown"
            size="xl"
            dialog-class="modal-dialog-pick-wi"
            data-test="modal-pick-work-item"
        >
            <div>
                <picker-sprints ref="sprint-picker" connection-context="backlog" @sprint-picked="searchWorkItemsBySprint"></picker-sprints>

                <!-- Missing Backlog Source Warning Card -->
                <card-missing-source
                    v-if="isMissingBacklogVisible"
                    source-type="backlog"
                    card-class="mt-3"
                    :project="selectedProject"
                    @configure-click="closeWindow"
                >
                    The active project <strong>"{{ selectedProject ? selectedProject.projectName : '' }}"</strong> does not have a backlog source connected.
                    Backlog tracking was skipped during project setup.
                </card-missing-source>

                <b-overlay v-else :show="loadingSearchResults" rounded="lg" opacity="0.6" class="pick-grid-overlay" :data-state="loadingSearchResults ? 'loading' : 'idle'">
                    <b-row class="my-1 no-gutters">
                        <b-col cols="12" class="p-0">
                            <!--Work Items Grid (headers & filters stay persistent)-->
                            <grid-shell
                                class="wi-pick-grid"
                                :columns="gridColumnsPick"
                                :loading="loadingSearchResults"
                                :empty="!loadingSearchResults && (!selectedSprint || !visibleSearchResults.length)"
                                :has-active-filters="isAnyFilterActive"
                                :empty-title="pickEmptyTitle"
                                :empty-message="pickEmptyMessage"
                                :empty-suggestions="selectedSprint ? workitemFilterSuggestions : []"
                                :empty-actions="selectedSprint ? workitemEmptyActions : []"
                                @reset-filters="resetToDefaults"
                                @empty-action="onEmptyAction"
                            >
                                <template #filters>
                                    <div class="filter-toolbar-label mr-2 flex-shrink-0">
                                        <i class="bi bi-funnel mr-1"></i><strong>Filters:</strong>
                                    </div>

                                    <!-- In-grid search filter -->
                                    <b-input-group size="sm" class="wi-search-filter mr-2 flex-shrink-1" style="max-width: 250px; min-width: 160px;">
                                        <b-form-input
                                            v-model="searchTerm"
                                            placeholder="Search title or ID..."
                                            data-test="input-picker-search"
                                            @keydown.enter.prevent="applyFilters"
                                            style="height: 31px; font-size: 0.82rem;"
                                        ></b-form-input>
                                        <b-input-group-append>
                                            <b-button size="sm" variant="outline-secondary" v-if="searchTerm" @click="clearSearch" aria-label="Clear search" title="Clear search" data-test="btn-clear-search">
                                                <i class="bi bi-x"></i>
                                            </b-button>
                                        </b-input-group-append>
                                    </b-input-group>

                                    <!-- Assigned filter chip -->
                                    <b-dropdown size="sm" :variant="isAssignedFilterActive ? 'primary' : 'outline-secondary'" no-caret class="grid-filter-chip mr-2 flex-shrink-0" data-test="filter-chip-assigned">
                                        <template #button-content>
                                            <i :class="isAssignedFilterActive ? 'bi bi-person-fill mr-1' : 'bi bi-people-fill mr-1'"></i>
                                            <span class="font-weight-bold mr-1">Assigned:</span>
                                            <span>{{ showAnyAssigned ? 'All Team Members' : 'Only Me' }}</span>
                                            <i class="bi bi-chevron-down ml-1 filter-chevron"></i>
                                        </template>
                                        <b-dropdown-item-button :active="!showAnyAssigned" @click="showAnyAssigned = false">
                                            <i class="bi bi-person mr-2"></i>Only Me
                                        </b-dropdown-item-button>
                                        <b-dropdown-item-button :active="showAnyAssigned" @click="showAnyAssigned = true">
                                            <i class="bi bi-people-fill mr-2"></i>All Team Members
                                        </b-dropdown-item-button>
                                    </b-dropdown>

                                    <!-- State filter chip -->
                                    <b-dropdown size="sm" :variant="isStatusFilterActive ? 'primary' : 'outline-secondary'" no-caret class="grid-filter-chip mr-2 flex-shrink-0" data-test="filter-chip-status">
                                        <template #button-content>
                                            <i :class="isStatusFilterActive ? 'bi bi-funnel-fill mr-1' : 'bi bi-funnel mr-1'"></i>
                                            <span class="font-weight-bold mr-1">State:</span>
                                            <span>{{ statusFilterText }}</span>
                                            <i class="bi bi-chevron-down ml-1 filter-chevron"></i>
                                        </template>
                                        <b-dropdown-form class="filter-chip-dropdown-form p-2">
                                            <div class="d-flex justify-content-between align-items-center mb-2 pb-1 border-bottom">
                                                <small class="font-weight-bold">Filter by State</small>
                                                <div>
                                                    <b-button size="sm" variant="link" class="p-0 text-decoration-none" @click="selectAllStatuses">All</b-button>
                                                </div>
                                            </div>
                                            <div class="filter-options-list">
                                                <b-form-checkbox
                                                    v-for="opt in allStatusOptions"
                                                    :key="opt"
                                                    :checked="statusFilter.includes(opt)"
                                                    :disabled="statusFilter.length <= 1 && statusFilter.includes(opt)"
                                                    :title="statusFilter.length <= 1 && statusFilter.includes(opt) ? 'At least one state must be selected' : ''"
                                                    class="m-0 compact-checkbox d-flex align-items-center"
                                                    @change="toggleStatusFilter(opt, $event)"
                                                >
                                                    <status-badge :status="opt" class="ml-1" />
                                                </b-form-checkbox>
                                            </div>
                                        </b-dropdown-form>
                                    </b-dropdown>

                                    <!-- Action Buttons aligned to the right -->
                                    <div class="filter-toolbar-actions ml-auto d-flex align-items-center flex-shrink-0">
                                        <!-- Apply Button -->
                                        <b-button size="sm" variant="primary" class="mr-2"
                                            :class="{ 'btn-apply-pulse': hasUnappliedFilters && !loadingSearchResults }"
                                            @click="applyFilters" :disabled="loadingSearchResults || isStatusFilterInvalid"
                                            title="Apply filters and reload from server" data-test="btn-apply-filters"
                                            :data-dirty="hasUnappliedFilters ? 'true' : 'false'"
                                            :data-state="loadingSearchResults ? 'loading' : 'idle'">
                                            <i class="bi bi-check-circle mr-1"></i>Apply
                                        </b-button>

                                        <!-- Reset to Defaults Button -->
                                        <b-button size="sm" variant="outline-secondary" @click="resetToDefaults" :disabled="loadingSearchResults" title="Reset all filters to defaults" data-test="btn-reset-filters">
                                            <i class="bi bi-arrow-counterclockwise mr-1"></i>Reset to Defaults
                                        </b-button>
                                    </div>
                                </template>

                                <template #header-state>
                                    <div class="d-flex align-items-center justify-content-center">
                                        <span>State</span>
                                        <i v-if="isStatusFilterActive" class="bi bi-funnel-fill column-filter-icon ml-1" v-b-tooltip.hover.top="'Filter applied'"></i>
                                    </div>
                                </template>

                                <b-row align-h="start" align-v="center" v-for="item in visibleSearchResults" :key="item.workItemId" :class="['my-1', 'grid-row', { 'disabled-row': item.isDisabled() }]" data-test="grid-row-pick-wi" :data-test-id="item.workItemId">
                                    <b-col class="grid-col-id">
                                        <nav-link-button :href="item.url" :id="item.workItemId"
                                                         :type-icon="item.workItemTypeIcon"
                                                         :work-item-type="item.workItemType"
                                                         title="Open work item in Azure DevOps" />
                                    </b-col>
                                    <b-col class="grid-col-title">
                                        <span class="title-text">{{item.title}}</span>
                                    </b-col>
                                    <b-col class="grid-col-state">
                                        <status-badge :status="item.state" />
                                    </b-col>
                                    <b-col class="grid-col-progress">
                                        <time-progress-item compact
                                                            :value="item.getFullCompletedHours()"
                                                            :total="item.getFullCapacityHours()"/>
                                    </b-col>
                                    <b-col class="grid-col-buttons action-buttons">
                                        <b-button size="sm" class="md-4" variant="info" @click="confirmWorkItem(item)" aria-label="Pick work item" data-test="btn-confirm-pick" v-b-tooltip.hover="pickTooltipConfig">
                                            <i class="bi bi-check2"></i>
                                        </b-button>
                                    </b-col>
                                </b-row>

                            </grid-shell>
                        </b-col>
                    </b-row>
                    <template #overlay>
                        <load-spinner text="Searching work items..." />
                    </template>
                </b-overlay>
            </div>
        </b-modal>
    </div>
</template>

<script>
    import _ from 'lodash';
    import sharedMixin from '../mixinAccount';
    import toastMixin from '../mixinToast';
    import commonMixin from '../mixinCommon';
    import axios from 'axios';
    import PickerSprints from './picker-sprints.vue';
    import GridShell from './grid-shell.vue';
    import TimeProgressItem from './time-progress-item.vue';
    import NavLinkButton from './nav-link-button.vue';
    import StatusBadge from './status-badge.vue';
    import CardMissingSource from './card-missing-source.vue';
    import workItemStateMixin from '../mixinWorkItemState';

    export default {
        mixins: [sharedMixin, toastMixin, commonMixin, workItemStateMixin],
        components: {
            'picker-sprints': PickerSprints,
            'grid-shell': GridShell,
            'time-progress-item': TimeProgressItem,
            'nav-link-button': NavLinkButton,
            'status-badge': StatusBadge,
            'card-missing-source': CardMissingSource
        },
        data() {
            return {
                modelFindWorkItem: null,
                searchTerm: '',
                showAnyAssigned: false,
                loadingSearchResults: false,
                pickWiAbortController: null,
                searchResults: [],
                baseStatusOptions: ["New", "To Do", "Proposed", "Active", "In Progress", "Doing", "Resolved", "Done", "Closed", "Removed"],
                statusFilter: ["New", "To Do", "Proposed", "Active", "In Progress", "Doing", "Resolved", "Done", "Closed"],
                appliedSearchTerm: '',
                appliedShowAnyAssigned: false,
                appliedStatusFilter: ["New", "To Do", "Proposed", "Active", "In Progress", "Doing", "Resolved", "Done", "Closed"],
                newTimeItemInstance: null,
                onAppliedChanges: null,
                selectedSprint: "",
                gridColumnsPick: [
                    { key: "id", label: "Work Item", width: "90px" },
                    { key: "title", label: "Title", width: "minmax(14rem, 1fr)", headerClass: "text-left pl-1" },
                    { key: "state", label: "State", width: "115px" },
                    { key: "progress", label: "Logged Progress", width: "170px" },
                    { key: "buttons", label: "Action", width: "65px" }
                ]
            };
        },
        computed: {
            allStatusOptions() {
                const projId = this.selectedProject?.backlogProjectId;
                const dynamicOptions = this.taskStateOptions(projId);
                const set = new Set(dynamicOptions && dynamicOptions.length > 0 ? dynamicOptions : this.baseStatusOptions);
                (this.searchResults || []).forEach(i => {
                    if (i.state) set.add(i.state);
                });
                return Array.from(set);
            },
            selectedProject() {
                return this.globalProject;
            },
            projectResolved() {
                return !!this.globalProject;
            },
            isMissingBacklogVisible() {
                return this.projectResolved && !!this.selectedProject && (!this.selectedProject.backlogLinkId || !this.selectedProject.backlogProjectId);
            },
            isAssignedFilterActive() {
                return !this.showAnyAssigned;
            },
            hasUnappliedFilters() {
                if ((this.searchTerm || '') !== (this.appliedSearchTerm || '')) return true;
                if (this.showAnyAssigned !== this.appliedShowAnyAssigned) return true;
                if (!this.areArraysEqual(this.statusFilter, this.appliedStatusFilter)) return true;
                return false;
            },
            isStatusFilterInvalid() {
                return !this.statusFilter || this.statusFilter.length === 0;
            },
            isStatusFilterActive() {
                return this.statusFilter.length > 0 && this.statusFilter.length < this.allStatusOptions.length;
            },
            statusFilterText() {
                if (!this.statusFilter || !this.statusFilter.length) return "Invalid";
                if (this.statusFilter.length === this.allStatusOptions.length) return "All";
                return this.statusFilter.join(", ");
            },
            visibleSearchResults() {
                return this.searchResults || [];
            },
            pickEmptyTitle() {
                if (!this.selectedSprint) {
                    return 'Select a sprint';
                }
                return 'No work items match your search';
            },
            pickEmptyMessage() {
                if (!this.selectedSprint) {
                    return 'Choose an active or past iteration above to view and filter backlog work items.';
                }
                return 'No backlog items match the current title search, assignment, or status filters in this sprint.';
            },
            isAnyFilterActive() {
                return !!(this.searchTerm && this.searchTerm.trim()) || this.isAssignedFilterActive || this.isStatusFilterActive;
            },
            workitemFilterSuggestions() {
                const suggestions = [];
                if (this.searchTerm && this.searchTerm.trim()) {
                    suggestions.push({
                        text: `Clear or broaden title search "${this.searchTerm.trim()}".`,
                        action: 'clear-search',
                        actionLabel: 'Clear Search'
                    });
                }
                if (!this.showAnyAssigned) {
                    suggestions.push({
                        text: "Switch assignment filter to 'Any Assigned' to see backlog items assigned to other team members.",
                        action: 'any-assigned',
                        actionLabel: 'Show Any Assigned'
                    });
                }
                if (this.isStatusFilterActive) {
                    suggestions.push({
                        text: `Enable all work item statuses (${this.allStatusOptions.join(', ')}).`,
                        action: 'all-statuses',
                        actionLabel: 'All Statuses'
                    });
                }
                suggestions.push("Select a different sprint above to search other iterations.");
                return suggestions;
            },
            workitemEmptyActions() {
                const actions = [];
                if (!this.showAnyAssigned) {
                    actions.push({
                        text: 'Show Any Assigned',
                        icon: 'bi bi-people',
                        action: 'any-assigned',
                        variant: 'outline-secondary'
                    });
                }
                return actions;
            },
            pickTooltipConfig() {
                if (typeof window !== 'undefined') {
                    const isMobile = window.innerWidth < 768 || (window.innerWidth < 992 && window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)').matches);
                    if (isMobile) {
                        return { disabled: true };
                    }
                }
                return {
                    title: 'Pick',
                    placement: 'top',
                    triggers: 'hover',
                    container: 'body',
                    boundary: 'window',
                    delay: { show: 150, hide: 0 },
                    interactive: false,
                    customClass: 'action-hover-pop modal-hover-pop'
                };
            }
        },
        mounted() {            
            this.modelFindWorkItem = this.$refs['md-find-work-item'];
        },
        beforeUnmount() {
            if (this.pickWiAbortController) {
                this.pickWiAbortController.abort();
                this.pickWiAbortController = null;
            }
        },
        methods: {
            selectAllStatuses() {
                this.statusFilter = [...this.allStatusOptions];
            },
            toggleStatusFilter(opt, isChecked) {
                if (isChecked) {
                    if (!this.statusFilter.includes(opt)) {
                        this.statusFilter = [...this.statusFilter, opt];
                    }
                } else {
                    if (this.statusFilter.length <= 1) return;
                    this.statusFilter = this.statusFilter.filter(s => s !== opt);
                }
            },
            clearSearch() {
                this.searchTerm = '';
                this.applyFilters();
            },
            resetToDefaults() {
                this.searchTerm = '';
                this.showAnyAssigned = false;
                const projId = this.selectedProject?.backlogProjectId;
                const dynamicOptions = this.taskStateOptions(projId);
                const base = dynamicOptions && dynamicOptions.length > 0 ? dynamicOptions : ["New", "To Do", "Proposed", "Active", "In Progress", "Doing", "Resolved", "Done", "Closed"];
                this.statusFilter = base.filter(s => !this.isTaskRemoved(projId, "Task", s));
                this.applyFilters();
            },
            resetFilters() {
                this.resetToDefaults();
            },
            onEmptyAction(actionKey) {
                if (actionKey === 'clear-search') {
                    this.clearSearch();
                } else if (actionKey === 'any-assigned') {
                    this.showAnyAssigned = true;
                    this.applyFilters();
                } else if (actionKey === 'all-statuses') {
                    this.selectAllStatuses();
                    this.applyFilters();
                }
            },
            areArraysEqual(a, b) {
                if (!a && !b) return true;
                if (!a || !b) return false;
                if (a.length !== b.length) return false;
                const sortedA = [...a].sort();
                const sortedB = [...b].sort();
                return sortedA.every((v, i) => v === sortedB[i]);
            },
            async applyFilters() {
                this.appliedSearchTerm = this.searchTerm || '';
                this.appliedShowAnyAssigned = this.showAnyAssigned;
                this.appliedStatusFilter = [...(this.statusFilter || [])];
                await this.fetchWorkItems();
            },
            pickWorkItem(newInstanceCb, onConfirmCb) {
                this.newTimeItemInstance = newInstanceCb;
                this.onAppliedChanges = onConfirmCb;
                this.modelFindWorkItem.show();
            },
            confirmWorkItem(confirmedItem) {
                this.$root.$emit('bv::hide::tooltip');
                this.modelFindWorkItem.hide();

                if (typeof this.onAppliedChanges === "function")
                    this.onAppliedChanges(confirmedItem);
            },
            closeWindow() {
                this.$root.$emit('bv::hide::tooltip');
                this.modelFindWorkItem.hide();
            },
            async onModelShown() {
                const projId = this.selectedProject?.backlogProjectId;
                if (projId) {
                    await this.ensureWorkflowsAsync(projId);
                    const dynamicOptions = this.taskStateOptions(projId);
                    if (dynamicOptions && dynamicOptions.length > 0) {
                        this.statusFilter = dynamicOptions.filter(s => !this.isTaskRemoved(projId, "Task", s));
                    }
                }
                const sprintPicker = this.$refs['sprint-picker'];
                if (sprintPicker && typeof sprintPicker.syncSelectedSprint === 'function') {
                    sprintPicker.syncSelectedSprint();
                }
                const sprintToLoad = this.$store.state.lastPickedSprint || this.selectedSprint;
                if (sprintToLoad) {
                    await this.searchWorkItemsBySprint(sprintToLoad);
                } else {
                    this.stopSpinners();
                }
            },
            applySearchResults(searchItems) {
                const self = this;
                const projId = this.selectedProject?.backlogProjectId;
                let stateComparer = function (obj) {
                    const cat = self.stateCategoryFor({ projectId: projId, workItemType: obj.workItemType || 'Task', state: obj.state });
                    switch ((cat || '').toLowerCase()) {
                        case 'proposed': return 0;
                        case 'inprogress': return 1;
                        case 'resolved': return 2;
                        case 'completed': return 3;
                        case 'removed': return 4;
                        default: return 100;
                    }
                };
                let orderedSearchItems = _.orderBy(searchItems, [stateComparer, 'createdDate'], ['asc', 'desc']);
                this.searchResults = [];
                _.forEach(orderedSearchItems, (si) => {
                    si.isDisabled = function () {
                        return self.isTaskRemoved(projId, si.workItemType, this.state);
                    };
                    let newTI = this.newTimeItemInstance(this.TIME_ITEM_TYPE_WORK);
                    _.assign(newTI, si);
                    if (this.selectedProject) {
                        newTI.projectId = this.selectedProject.backlogProjectId || this.selectedProject.projectId;
                        newTI.projectName = this.selectedProject.backlogProjectName || this.selectedProject.projectName;
                        newTI.platformType = this.selectedProject.platformType;
                        newTI.organizationId = this.selectedProject.organizationId;
                    }
                    this.searchResults.push(newTI);
                });
            },
            wait(ms) {
                return new Promise(resolve => setTimeout(resolve, ms));
            },
            async searchWorkItemsBySprint(sprint) {
                this.selectedSprint = sprint;
                await this.fetchWorkItems();
            },
            async fetchWorkItems() {
                if (!this.selectedSprint) {
                    this.searchResults = [];
                    this.stopSpinners();
                    return;
                }
                if (this.pickWiAbortController) {
                    this.pickWiAbortController.abort();
                    this.pickWiAbortController = null;
                }
                const controller = new AbortController();
                this.pickWiAbortController = controller;

                const params = new URLSearchParams();
                params.append('sprint', this.selectedSprint);
                params.append('anyAssigned', this.showAnyAssigned);

                if (this.statusFilter && this.statusFilter.length > 0 && this.statusFilter.length < this.allStatusOptions.length) {
                    params.append('statesCSV', this.statusFilter.join(','));
                }
                if (this.searchTerm && this.searchTerm.trim()) {
                    params.append('term', this.searchTerm.trim());
                }
                if (this.selectedProject && this.selectedProject.projectId) {
                    params.append('projectId', this.selectedProject.projectId);
                }

                this.appliedSearchTerm = this.searchTerm || '';
                this.appliedShowAnyAssigned = this.showAnyAssigned;
                this.appliedStatusFilter = [...(this.statusFilter || [])];
                this.loadingSearchResults = true;
                try {
                    const resp = await this.$http.get('/TimeManagement/FilterWorkItems', {
                        params,
                        signal: controller.signal
                    });
                    if (resp && resp.data && resp.data.length) {
                        this.applySearchResults(resp.data);
                    } else {
                        this.searchResults = [];
                    }
                } catch (e) {
                    if (axios.isCancel(e) || e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') {
                        return;
                    }
                    this.searchResults = [];
                    const errMsg = e?.response?.data?.message || e?.message || 'Failed to filter work items';
                    this.toastError('Filter Work Items Error', errMsg);
                } finally {
                    if (this.pickWiAbortController === controller) {
                        this.stopSpinners();
                        this.pickWiAbortController = null;
                    }
                }
            },
            stopSpinners() {
                this.loadingSearchResults = false;
            },
        },
    };
</script>
<style scoped>
    @media (min-width: 768px) {
        ::v-deep .modal-dialog-pick-wi {
            max-width: min(1240px, 94vw) !important;
            width: 94vw !important;
            margin: 1.5rem auto !important;
        }
    }
    @media (min-width: 1400px) {
        ::v-deep .modal-dialog-pick-wi {
            max-width: 1300px !important;
        }
    }
    .wi-pick-grid {
        padding-left: 0 !important;
        padding-right: 0 !important;
        width: 100% !important;
    }
    .wi-pick-grid ::v-deep .grid-shell {
        width: 100% !important;
        padding-left: 0;
        padding-right: 0;
    }
    .wi-pick-grid ::v-deep .grid-shell-scroll {
        width: 100% !important;
        min-height: 320px;
        max-height: calc(100vh - 280px);
        overflow-x: auto;
    }
    .wi-pick-grid ::v-deep .grid-shell-content {
        width: 100% !important;
        min-width: 100% !important;
        display: flex;
        flex-direction: column;
    }
    .wi-pick-grid ::v-deep .grid-shell-header,
    .wi-pick-grid ::v-deep .card,
    .wi-pick-grid ::v-deep .grid-row {
        width: 100% !important;
        min-width: 100% !important;
        box-sizing: border-box;
    }
    .wi-pick-grid .grid-row {
        align-items: center;
    }
    .wi-pick-grid .grid-col-id {
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: visible;
    }
    .wi-pick-grid .grid-col-title {
        text-align: left;
        line-height: 1.3;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: normal;
        word-break: break-word;
        overflow-wrap: anywhere;
        padding-right: 0.5rem;
    }
    .wi-pick-grid .grid-col-state {
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .wi-pick-grid .grid-col-progress {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        overflow: hidden;
    }
    .wi-pick-grid .grid-col-buttons {
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: visible;
    }
    .column-filter-icon {
        color: #ffc107;
        font-size: 0.75rem;
    }
    .pick-grid-overlay {
        min-height: 320px;
        width: 100%;
        display: block;
    }
    .filter-options-list {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }
    .compact-checkbox {
        font-size: 0.82rem;
        line-height: 1.3;
        margin-bottom: 0 !important;
        width: 100%;
    }
    .compact-checkbox >>> .custom-control-label {
        cursor: pointer;
        font-size: 0.82rem;
        padding-top: 1px;
        user-select: none;
        white-space: nowrap;
        width: 100%;
    }
    .compact-checkbox >>> .custom-control-input:disabled ~ .custom-control-label {
        cursor: not-allowed;
        opacity: 0.65;
    }
</style>

<style>
    /* Pick Work Item Modal Mobile Card Layout (<=991.98px) matching Logged WI Grid */
    @media (max-width: 991.98px) {
        .wi-pick-grid .grid-row {
            background-color: var(--vt-surface-card, rgba(30, 41, 59, 0.45));
            border: 1px solid var(--vt-border, rgba(148, 163, 184, 0.22));
            border-radius: 0.65rem;
            padding: 0.75rem 0.85rem !important;
            margin-bottom: 0.75rem !important;
            box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
            display: flex !important;
            flex-wrap: wrap !important;
            align-items: flex-start !important;
            gap: 0.4rem 0.65rem !important;
            height: auto !important;
            transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }

        [data-theme="dark"] .wi-pick-grid .grid-row {
            background-color: rgba(30, 41, 59, 0.65) !important;
            border-color: rgba(148, 163, 184, 0.24) !important;
        }

        [data-theme="light"] .wi-pick-grid .grid-row {
            background-color: #ffffff !important;
            border-color: rgba(203, 213, 225, 0.85) !important;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
        }

        /* Top row of card: ID + State Badge on left, Pick action on right */
        .wi-pick-grid .grid-row > .grid-col-id {
            order: 1 !important;
            flex: 0 0 auto !important;
            width: auto !important;
            max-width: none !important;
            padding: 0 !important;
        }

        .wi-pick-grid .grid-row > .grid-col-state {
            order: 1 !important;
            flex: 0 0 auto !important;
            width: auto !important;
            max-width: none !important;
            padding: 0 !important;
            margin-left: 0.25rem;
        }

        .wi-pick-grid .grid-row > .grid-col-buttons {
            order: 1 !important;
            flex: 0 0 auto !important;
            width: auto !important;
            max-width: none !important;
            padding: 0 !important;
            margin-left: auto !important;
        }

        .wi-pick-grid .grid-row > .grid-col-buttons .btn {
            min-width: 36px;
            height: 30px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0.25rem 0.6rem !important;
            font-size: 0.95rem !important;
            border-radius: 0.4rem !important;
        }

        /* Second row of card: Full-width Title */
        .wi-pick-grid .grid-row > .grid-col-title {
            order: 2 !important;
            flex: 0 0 100% !important;
            width: 100% !important;
            max-width: 100% !important;
            padding: 0 !important;
            margin-top: 0.2rem;
            margin-bottom: 0.35rem;
            text-align: left !important;
        }

        .wi-pick-grid .grid-col-title .title-text {
            font-size: 0.95rem;
            font-weight: 600;
            color: #0f172a;
            line-height: 1.35;
        }

        [data-theme="dark"] .wi-pick-grid .grid-col-title .title-text {
            color: #f8fafc !important;
        }

        /* Third row of card: Full-width Progress Bar */
        .wi-pick-grid .grid-row > .grid-col-progress {
            order: 3 !important;
            flex: 0 0 100% !important;
            width: 100% !important;
            max-width: 100% !important;
            padding: 0 !important;
            margin-top: 0.1rem;
        }

        .wi-pick-grid .grid-row.disabled-row {
            opacity: 0.6;
            pointer-events: none;
        }
    }
</style>
